import { Box, Paper, Stack, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { CustomFieldValueWithDefinition } from '@vy/custom-fields-types';
import { notDefined } from '@vy/null';
import { VyTable } from '../../../../components/new-design/table/vy-table/VyTable';
import { VyTableCell } from '../../../../components/new-design/table/vy-table/VyTableCell';
import { isNotEmptyString } from '@vy/utils';
import { CustomerSettingsEditableCell } from './CustomerSettingsEditableCell';

type CustomerSettingsCustomFieldsProps = {
  customFields: CustomFieldValueWithDefinition[];
};

const getCustomFieldRowId = (row: CustomFieldValueWithDefinition): string => row.customFieldDefinitionId;

export const CustomerSettingsCustomFields: FC<CustomerSettingsCustomFieldsProps> = ({ customFields }) => {
  const indexByRowId = useMemo(() => {
    const map = new Map<string, number>();
    customFields.forEach((field, index) => {
      map.set(getCustomFieldRowId(field), index);
    });
    return map;
  }, [customFields]);

  const columns = useMemo<GridColDef<CustomFieldValueWithDefinition>[]>(
    () => [
      {
        field: 'source',
        headerName: 'Source',
        width: 160,
        sortable: false,
        valueGetter: (_value, row) => row.definition?.integrationSource ?? 'Vayu',
        renderCell: (params) => (
          <VyTableCell
            value={params.row.definition?.integrationSource ?? 'Vayu'}
            containerClassName="justify-start"
          />
        ),
      },
      {
        field: 'fieldPath',
        headerName: 'Source field',
        width: 240,
        sortable: false,
        valueGetter: (_value, row) => row.definition?.fieldPath ?? '---',
        renderCell: (params) => (
          <VyTableCell value={params.row.definition?.fieldPath} containerClassName="justify-start" />
        ),
      },
      {
        field: 'fieldName',
        headerName: 'Vayu field',
        width: 240,
        sortable: false,
        valueGetter: (_value, row) => (isNotEmptyString(row.fieldName) ? row.fieldName : '---'),
        renderCell: (params) => (
          <VyTableCell value={params.row.fieldName} containerClassName="justify-start" />
        ),
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const index = indexByRowId.get(getCustomFieldRowId(params.row));
          const valueType = params.row.definition?.valueType ?? 'String';
          const defaultValue = params.row.definition?.defaultValue;

          if (notDefined(index)) {
            return <span>---</span>;
          }

          return (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1
              }}>
              <CustomerSettingsEditableCell
                index={index}
                valueType={valueType}
                allowedValues={params.row.definition?.allowedValues ?? []}
              />
              {defaultValue && (
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Default: {defaultValue}
                </Typography>
              )}
            </Stack>
          );
        },
      },
    ],
    [indexByRowId],
  );

  return (
    <Paper
      elevation={1}
      sx={{
        p: '24px 32px',
        borderRadius: '10px',
        boxShadow: '0px 10px 24px rgba(0,0,0,0.03)',
        border: '1px solid rgba(13,27,39,0.1)',
        minWidth: '880px',
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          color: '#041E78',
          mb: 1
        }}>
        Custom fields
      </Typography>
      <Box>
        <VyTable
          tableName="customer-settings-custom-fields"
          items={customFields}
          itemsCount={customFields.length}
          columns={columns}
          showPagination={false}
          getRowId={getCustomFieldRowId}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              height: '40px !important',
              maxHeight: '40px !important',
              minHeight: '40px !important',
              borderBottom: '1px solid rgba(13, 27, 39, 0.10)',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: 0,
              lineHeight: '24px',
              height: '40px !important',
              maxHeight: '40px !important',
              minHeight: '40px !important',
            },
            '& .MuiDataGrid-cell': {
              padding: 0,
              borderBottom: '1px solid rgba(13, 27, 39, 0.10)',
            },
            '& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              height: '52px',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-row.Mui-hovered': {
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell': {
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: 'rgba(13, 27, 39, 0.10)',
            },
          }}
        />
      </Box>
    </Paper>
  );
};
